// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './StatusBar.module.css'
import { Trash2 as TrashIcon, Share2 as ShareIcon, Bug as BugIcon } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks'
import IconButton from '../IconButton/IconButton'
import { resetSession } from '../../services/system'
import { unshareSession, updateSession } from '../../services/session'
import { removeSession, upsertSession } from '../../store/slices/sessionSlice'
import { useMaskActions } from '../Canvas/MaskContext'
import { PrivacyToggle } from './PrivacyToggle'
import { ShareModal } from './ShareModal'
import { useAuth } from '../../store/hooks/useAuth'
import { closeTab } from '../../store/slices/sessionTabsSlice'
import { SessionTabs } from './SessionTabs'
import { selectActiveSession, selectActiveSessionId } from '../../store/selectors/sessionSelectors'
import { cancelSessionStream } from '../../services/sessionStreams/SessionStreamRegistry'
import { removeChatState } from '../../store/slices/chatSlice'
import { removeCanvasState } from '../../store/slices/canvasSlice'
import { removeSelectionState } from '../../store/slices/selectionSlice'

export function StatusBar(): ReactElement {
    const dispatch = useAppDispatch()
    const { t } = useTranslation('common')
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const openSessionIds = useAppSelector((s) => s.sessionTabs.openSessionIds)
    const maskActions = useMaskActions()
    const { user } = useAuth()
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [hasToolError, setHasToolError] = useState(false)
    const pendingError = useRef<{ toolName: string; displayName: string; errorType?: string } | null>(null)
    const isResetDisabled = !session || !activeSessionId

    const openSentryForm = useCallback(async (errorContext?: { toolName: string; displayName: string; errorType?: string }) => {
        if (errorContext) {
            Sentry.setTag('tool_error', errorContext.toolName)
            Sentry.setTag('error_type', errorContext.errorType ?? 'unknown')
        }
        const feedback = Sentry.getFeedback()
        const form = await feedback?.createForm({
            formTitle: errorContext ? t('errors:reportToolError') : t('reportBug'),
        })
        if (!form) return
        form.appendToDom()
        form.open()
        if (errorContext) {
            requestAnimationFrame(() => {
                const textarea = document.querySelector<HTMLTextAreaElement>('[name="message"]')
                if (textarea) {
                    const prefill = `[${errorContext.displayName}] ${errorContext.errorType ?? 'error'}`
                    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
                    nativeSetter?.call(textarea, prefill)
                    textarea.dispatchEvent(new Event('input', { bubbles: true }))
                }
            })
        }
        setHasToolError(false)
        pendingError.current = null
    }, [t])

    useEffect(() => {
        const onToolError = (e: Event) => {
            const detail = (e as CustomEvent).detail
            pendingError.current = detail
            setHasToolError(true)
        }
        const onReportError = (e: Event) => {
            const detail = (e as CustomEvent).detail
            openSentryForm(detail)
        }
        window.addEventListener('tool:error', onToolError)
        window.addEventListener('report:error', onReportError)
        return () => {
            window.removeEventListener('tool:error', onToolError)
            window.removeEventListener('report:error', onReportError)
        }
    }, [openSentryForm])

    // Determine permissions
    const isOwner = !!(session && user && session.user_id === user.id)
    const canEdit = !!(
        session && user && (
            session.user_id === user.id ||
            (session.shared_with || []).includes(user.id)
        )
    )

    async function onDeleteSession(): Promise<void> {
        if (!session || !activeSessionId) return
        const nextActiveSessionId = computeNextActiveSessionId(openSessionIds, session.id)
        try {
            maskActions.resetMask?.()
            maskActions.setBrushEnabled?.(false)
        } catch { }
        cancelSessionStream(session.id)
        await resetSession(session.id)
        try { localStorage.removeItem('session_id') } catch { }
        dispatch(removeSession(session.id))
        dispatch(removeChatState({ sessionId: session.id }))
        dispatch(removeCanvasState({ sessionId: session.id }))
        dispatch(removeSelectionState({ sessionId: session.id }))
        dispatch(closeTab(session.id))
        if (nextActiveSessionId) {
            navigateToSession(nextActiveSessionId)
        } else {
            navigateToSessions()
        }
    }

    async function handlePrivacyToggle(newPrivacy: string) {
        if (!session) return

        // Optimistic update
        const updatedSession = { ...session, privacy: newPrivacy }
        dispatch(upsertSession(updatedSession as any))

        try {
            await updateSession(session.id, { privacy: newPrivacy })
        } catch (error) {
            console.error('Failed to update privacy', error)
            // Revert on error
            dispatch(upsertSession(session as any))
        }
    }

    async function handleRemoveUser(userId: string) {
        if (!session) return

        try {
            const updatedSession = await unshareSession(session.id, userId)
            // Preserve the full session structure (nodes, commits, messages)
            dispatch(upsertSession({ ...session, ...updatedSession } as any))
        } catch (e) {
            console.error('Failed to remove user', e)
            throw e
        }
    }

    return (
        <div className={styles.root}>
            <div className={styles.topRow}>
                <div className={styles.left}>
                    <a
                        href="/sessions"
                        className={styles.brandLink}
                        onClick={(e) => {
                            e.preventDefault()
                            navigateToSessions()
                        }}
                    >
                        {t('brandName')}
                    </a>
                    <div className={styles.tabsInline}>
                        <SessionTabs />
                    </div>
                </div>
                <div className={styles.right}>
                    {session && (
                        <PrivacyToggle
                            privacy={session.privacy}
                            canEdit={canEdit}
                            onToggle={handlePrivacyToggle}
                        />
                    )}
                    {session && (isOwner || canEdit) && (
                        <IconButton
                            className={styles.shareButton}
                            onClick={() => setIsShareModalOpen(true)}
                            title={t('shareSession')}
                            aria-label={t('shareSession')}
                        >
                            <ShareIcon className={styles.icon} size={18} aria-hidden="true" />
                        </IconButton>
                    )}
                    {isOwner && (
                        <IconButton
                            className={styles.resetButton}
                            onClick={onDeleteSession}
                            disabled={isResetDisabled}
                            title={isResetDisabled ? t('noSession') : t('deleteSession')}
                            aria-label={t('deleteSession')}
                        >
                            <TrashIcon className={styles.resetIcon} aria-hidden="true" />
                        </IconButton>
                    )}
                    <IconButton
                        className={`${styles.bugButton} ${hasToolError ? styles.bugButtonAlert : ''}`}
                        onClick={() => openSentryForm(pendingError.current ?? undefined)}
                        title={hasToolError ? t('errors:reportToolError') : t('reportBug')}
                        aria-label={hasToolError ? t('errors:reportToolError') : t('reportBug')}
                    >
                        <BugIcon className={styles.icon} size={18} aria-hidden="true" />
                    </IconButton>
                </div>
            </div>

            {session && (
                <ShareModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    sessionId={session.id}
                    canManage={isOwner}
                    privacy={session.privacy}
                    onPrivacyChange={(newPrivacy) => {
                        const updated = { ...session, privacy: newPrivacy }
                        dispatch(upsertSession(updated as any))
                    }}
                    onRemoveUser={handleRemoveUser}
                />
            )}
        </div>
    )
}

function computeNextActiveSessionId(openSessionIds: string[], closingId: string): string | null {
    const idx = openSessionIds.indexOf(closingId)
    if (idx === -1) return openSessionIds[0] ?? null
    const remaining = openSessionIds.filter((id) => id !== closingId)
    return remaining[idx] ?? remaining[idx - 1] ?? null
}

function navigateToSessions(): void {
    try {
        // Keep SPA routing in sync with App.tsx's pathname state (popstate listener).
        window.history.replaceState({}, '', '/sessions')
        window.dispatchEvent(new PopStateEvent('popstate'))
    } catch {
        window.location.href = '/sessions'
    }
}

function navigateToSession(sessionId: string): void {
    try {
        window.history.pushState({}, '', `/session/${sessionId}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
        try { localStorage.setItem('session_id', sessionId) } catch { }
    } catch {
        window.location.href = `/session/${sessionId}`
    }
}
